import { initialsFor } from "./threadSummary";

import type {
  CorrespondenceAttachment,
  CorrespondenceMessage,
  CorrespondenceThreadEvent,
} from "@platform/api";

/** What one message card shows. */
export interface MessageSummary {
  kind: "message";
  id: string;
  /**
   * Who wrote it, as the card's heading.
   *
   * `You` where the message came from the address reading it, and the
   * sender's own name otherwise. The viewer's name is not used for the
   * comparison: two analysts share a display name far more readily than an
   * address, and attributing a colleague's reply to the reader is the worse
   * error.
   */
  author: string;
  initials: string;
  /** `Sep 12, 2026 · 10:45 AM`, in the viewer's locale. */
  at: string;
  /**
   * The text of the message.
   *
   * `bodyText` where there is one, the markdown an analyst or RUMI authored
   * next, and the words out of `bodyHtml` last. See `textOf`.
   */
  body: string;
  /**
   * True where the words were recovered from HTML rather than sent as text.
   *
   * The card says so, because what the reader sees is the message stripped of
   * how the sender laid it out: a rates table arrives as its cells in a row.
   */
  bodyFromHtml: boolean;
  /** Whether the signed-in analyst has read it. */
  read: boolean;
  /** Unsent, so the card says so rather than reading as part of the exchange. */
  isDraft: boolean;
  /** Authored by RUMI, which the card names per ADR-055. */
  byRumi: boolean;
  attachments: AttachmentSummary[];
  /** Orders the thread. See `timelineOf`. */
  position: number;
}

export interface AttachmentSummary {
  id: string;
  fileName: string;
  /** `PDF`, `XLSX` — the extension, which is what the design labels. */
  kind: string;
  /** `288 KB`, or empty where the size was not recorded. */
  size: string;
  /**
   * Whether the file can be handed to the reader.
   *
   * False everywhere today: serving one needs stored bytes and a clean virus
   * scan, and nothing scans yet (CD-2989). The row names the file either
   * way, because what arrived is evidence even while it cannot be opened.
   */
  downloadReady: boolean;
  /** Set when the scan has something to say about why it cannot be opened. */
  unavailableReason?: string;
}

/** Something that happened to the thread which nobody wrote. */
export interface EventSummary {
  kind: "event";
  id: string;
  /** What happened, in words a reader can act on. */
  label: string;
  at: string;
  /** Whether it is bad news, which decides the row's treatment. */
  isProblem: boolean;
  position: number;
}

export type TimelineEntry = MessageSummary | EventSummary;

/**
 * What each event type means to somebody reading the conversation.
 *
 * Spelled out rather than humanised from the enum: a reader seeing "Auto reply"
 * would take the thread as answered, which is the misreading the separate table
 * exists to prevent.
 *
 * The wording states what happened and stops there. An automatic reply is not a
 * read receipt — it can arrive after a person has read the mail, and it says
 * nothing about the other recipients — so the row must not imply one. Nothing
 * records who has read what.
 */
const EVENT_TREATMENTS: Record<string, { label: string; isProblem: boolean }> =
  {
    auto_reply: {
      label: "An automatic reply was received",
      isProblem: false,
    },
    delivery_failed: {
      label: "This message was not delivered",
      isProblem: true,
    },
    quarantined: {
      label: "A message arrived and is being held for review",
      isProblem: true,
    },
    attachment_infected: {
      label: "An attachment failed its virus scan and was not stored",
      isProblem: true,
    },
    reopened: { label: "The conversation was reopened", isProblem: false },
    rfp_relinked: {
      label: "This thread was moved to another RFP",
      isProblem: false,
    },
  };

/**
 * Why an attachment cannot be opened, where the scan knows.
 *
 * `clean` is absent: a clean file that is still unavailable is waiting on the
 * storage half of CD-2989, which is not something to explain per row.
 */
const SCAN_REASONS: Record<string, string> = {
  pending: "Waiting on a virus scan",
  infected: "Failed its virus scan",
  failed: "Could not be scanned",
};

const BYTES_PER_UNIT = 1024;
const SIZE_UNITS = ["bytes", "KB", "MB", "GB"] as const;

/**
 * A file size the way a mail client writes one.
 *
 * Absent and zero both render empty. A zero-byte attachment is a parse
 * result rather than a fact about the file, and `0 bytes` beside a name reads
 * as a claim that the sender attached nothing.
 */
export const formatBytes = (bytes: number | undefined): string => {
  if (!bytes || bytes < 0) return "";

  let size = bytes;
  let unit = 0;

  while (size >= BYTES_PER_UNIT && unit < SIZE_UNITS.length - 1) {
    size /= BYTES_PER_UNIT;
    unit += 1;
  }

  // Whole bytes, and one decimal past that — 288 KB rather than 288.3 KB is
  // what the design shows, but 1.4 MB loses too much as 1 MB.
  const rounded = unit === 0 ? size : Math.round(size * 10) / 10;

  return `${rounded} ${SIZE_UNITS[unit]}`;
};

/**
 * The extension, uppercased, as the attachment row's type label.
 *
 * Taken from the file name rather than the content type: a carrier's mail
 * system routinely sends `application/octet-stream`, and the name is what the
 * sender actually called the file.
 */
export const fileKind = (fileName: string): string => {
  const dot = fileName.lastIndexOf(".");

  if (dot <= 0 || dot === fileName.length - 1) return "FILE";

  return fileName.slice(dot + 1).toUpperCase();
};

/**
 * When a message entered the conversation.
 *
 * `receivedAt` is when we saw it, and `createdAt` stands in for everything
 * outbound, which we never receive. `sentAt` is deliberately not used: it is
 * what the far end claims, so a carrier with a wrong clock would reorder our
 * history. This is the same rule the service orders by, restated here because
 * the view interleaves events with messages and has to rank both.
 */
const messagePosition = (message: CorrespondenceMessage): number =>
  new Date(message.receivedAt ?? message.createdAt ?? 0).getTime();

export const formatMessageTime = (
  timestamp: string | undefined,
  locale?: string,
): string => {
  if (!timestamp) return "";

  const at = new Date(timestamp);

  if (Number.isNaN(at.getTime())) return "";

  const day = at.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = at.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
  });

  return `${day} · ${time}`;
};

/**
 * Tags whose text is markup rather than prose, so it goes with the tag.
 *
 * A stylesheet and the body of a script are not things the carrier wrote, and
 * read out beside their message they look like things the carrier wrote.
 */
const NON_PROSE_TAGS =
  "script,style,noscript,template,head,title,meta,link,iframe,object,embed,img,picture,source,video,audio,track";

/**
 * Tags that end a line, so the words either side of them stay apart.
 *
 * `textContent` concatenates with no separator, so two paragraphs come back as
 * one run-on. Each of these gets a newline after it before the text is read.
 */
const BLOCK_TAGS =
  "p,div,br,li,tr,h1,h2,h3,h4,h5,h6,blockquote,pre,table,ul,ol";

/**
 * Cells, which end a value rather than a line.
 *
 * Without a separator `<td>1</td><td>234</td>` reads as `1234`, turning two
 * figures into one — and a rate table is the shape most likely to arrive as
 * HTML with no text part, so this is the case that matters most. A tab,
 * because the card renders with `pre-wrap` and a space between two numbers is
 * itself ambiguous.
 */
const CELL_TAGS = "td,th";

/**
 * The words in a message, whatever shape it arrived in.
 *
 * `bodyText` first, then an analyst's or RUMI's markdown, then the text inside
 * `bodyHtml`. The last is the case this exists for: the inbound parser fills
 * `body_text` only from a `text/plain` part and derives nothing from HTML, so
 * a carrier whose system sends `text/html` alone stored its whole message in a
 * column nothing read. Those cards rendered blank.
 *
 * The HTML is read for its words and never rendered, which is a different job
 * from sanitising markup for display and is why DOMPurify is not in this path.
 *
 * It is parsed into a `<template>`, whose contents the HTML spec places in a
 * separate inert document — inert by specification rather than as a
 * consequence of having no browsing context, which is the stronger thing to
 * rest on when the input is a stranger's markup. Nothing in it loads and
 * nothing in it runs, and the removal below is a second line rather than the
 * only one: by the time any element is removed, parsing has already happened,
 * so a construct that fetched during parsing could not be fixed afterwards.
 * Every tag that could carry a URL is removed regardless, so there is nothing
 * left to request either way.
 *
 * Nothing is attached to the page, and what comes back out is `textContent` —
 * a string, handed to React as a string, so no markup reaches the DOM whatever
 * the message contained.
 *
 * Deriving it here rather than in the inbound parser is deliberate. A parser
 * change would only help mail arriving after it ships, and every HTML-only
 * message already in the table would stay unreadable. This covers both. The
 * parser should still learn to do it, for RUMI drafting and anything else
 * reading `body_text` directly.
 */
export const textOf = (
  message: CorrespondenceMessage,
): { text: string; fromHtml: boolean } => {
  const authored = message.bodyText || message.bodyMarkdown;

  if (authored) return { text: authored, fromHtml: false };

  if (!message.bodyHtml) return { text: "", fromHtml: false };

  const inert = document.createElement("template");

  inert.innerHTML = message.bodyHtml;

  const parsed = inert.content;
  const owner = parsed.ownerDocument;

  for (const node of parsed.querySelectorAll(NON_PROSE_TAGS)) {
    node.remove();
  }

  for (const node of parsed.querySelectorAll(CELL_TAGS)) {
    node.after(owner.createTextNode("\t"));
  }

  // After the cells, so a row's trailing tab is followed by the newline and
  // collapses with it rather than opening the next row.
  for (const node of parsed.querySelectorAll(BLOCK_TAGS)) {
    node.after(owner.createTextNode("\n"));
  }

  // Runs of blank lines come from the block elements that were removed, and a
  // card opening on half a screen of nothing reads as a broken message.
  const text = (parsed.textContent ?? "")
    .replace(/\t+\n/gu, "\n")
    .replace(/[ \t]+$/gmu, "")
    .replace(/\n{3,}/gu, "\n\n")
    .trim();

  return { text, fromHtml: !!text };
};

export const toAttachmentSummary = (
  attachment: CorrespondenceAttachment,
): AttachmentSummary => ({
  id: attachment.id ?? "",
  fileName: attachment.fileName ?? "",
  kind: fileKind(attachment.fileName ?? ""),
  size: formatBytes(attachment.bytes),
  downloadReady: attachment.downloadReady ?? false,
  unavailableReason: attachment.downloadReady
    ? undefined
    : SCAN_REASONS[attachment.scanStatus ?? ""],
});

export const toMessageSummary = (
  message: CorrespondenceMessage,
  viewerAddress?: string,
  locale?: string,
): MessageSummary => {
  const from = message.fromAddress ?? "";
  const isOwn =
    !!viewerAddress && from.toLowerCase() === viewerAddress.toLowerCase();
  const name = message.fromName || from;
  const { text, fromHtml } = textOf(message);

  return {
    kind: "message",
    id: message.id ?? "",
    author: isOwn ? "You" : name,
    initials: initialsFor(name),
    at: formatMessageTime(message.receivedAt ?? message.createdAt, locale),
    body: text,
    bodyFromHtml: fromHtml,
    read: message.read ?? false,
    isDraft: message.status === "MESSAGE_STATUS_DRAFT",
    byRumi: message.author === "MESSAGE_AUTHOR_RUMI",
    attachments: (message.attachments ?? [])
      // An inline image is part of how the message was laid out — a
      // signature logo, usually — rather than a file somebody attached, and
      // listing it as one makes every carrier's footer look like evidence.
      .filter((attachment) => !attachment.isInline)
      .map(toAttachmentSummary),
    position: messagePosition(message),
  };
};

export const toEventSummary = (
  event: CorrespondenceThreadEvent,
  locale?: string,
): EventSummary | undefined => {
  const treatment = EVENT_TREATMENTS[event.type ?? ""];

  // An event type this build does not know about renders as nothing rather
  // than as its enum name. The row exists to tell a reader what happened, and
  // `rfp_relinked` in the middle of a conversation does not do that.
  if (!treatment) return undefined;

  return {
    kind: "event",
    id: event.id ?? "",
    label: treatment.label,
    isProblem: treatment.isProblem,
    at: formatMessageTime(event.occurredAt, locale),
    position: new Date(event.occurredAt ?? 0).getTime(),
  };
};

/**
 * The thread as the drawer reads it: messages and events together, newest
 * first.
 *
 * The service answers oldest first, which is the order the conversation was
 * lived. The drawer reverses it because an analyst opens a thread to see what
 * just arrived, and a long exchange would otherwise open on its first message.
 *
 * Events are interleaved rather than collected at one end. A bounce means the
 * message above it never arrived, and that only reads correctly next to the
 * message it is about.
 */
export const timelineOf = (
  messages: CorrespondenceMessage[],
  events: CorrespondenceThreadEvent[],
  viewerAddress?: string,
  locale?: string,
): TimelineEntry[] => {
  const entries: TimelineEntry[] = [
    ...messages.map((message) =>
      toMessageSummary(message, viewerAddress, locale),
    ),
    ...events
      .map((event) => toEventSummary(event, locale))
      .filter((entry): entry is EventSummary => !!entry),
  ];

  // By id after position, so two entries sharing a timestamp keep one order
  // across reads rather than swapping on each render.
  return entries.sort(
    (a, b) => b.position - a.position || a.id.localeCompare(b.id),
  );
};
